import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    Tabs,
    Tab,
    useTheme,
    Stack,
    Button,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert,
    Radio,
    Divider,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Switch,
    TextField,
    Grid,
    Chip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { collectionPointsService, CollectionPoint, CollectionPointPurposeMap, CollectionPointPurposeConstant } from '../../services/collectionpoints-service';
import { purposeService, Purpose } from '../../services/purpose-service';
import { config } from '../../config/config';
import LabelsTab from './LabelsTab';
import PreferencesTab from './PreferencesTab';
import DataElementsTab from './DataElementsTab';
import DetailsTab from './DetailsTab';
import PurposesTab from './PurposesTab';
import IntegrationsTab from './IntegrationsTab';
import EventsTab from './EventsTab';





interface EditCollectionPointPanelProps {
    open: boolean;
    onClose: () => void;
    collectionPoint: CollectionPoint | null;
    onSaved?: () => void; // Notify parent to refresh grid
}

const EditCollectionPointPanel: React.FC<EditCollectionPointPanelProps> = ({ open, onClose, collectionPoint, onSaved }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);



    // Track if any changes were saved (to know if grid needs refresh)
    const [hasAnyChangesSaved, setHasAnyChangesSaved] = useState(false);
    
    // Store purposes form state in parent to persist across tab switches
    const [purposesFormState, setPurposesFormState] = useState<any>(null);
    
    // Store purposes data in parent to persist across tab switches
    const [purposesData, setPurposesData] = useState<{
        purposes: any[];
        purposeConstants: any[];
        mappedPurposes: any[];
        hasLoadedInitialData: boolean;
        hasAttemptedLoadMappedPurposes: boolean;
    }>({
        purposes: [],
        purposeConstants: [],
        mappedPurposes: [],
        hasLoadedInitialData: false,
        hasAttemptedLoadMappedPurposes: false
    });
    
    // Store details data in parent to persist across tab switches
    const [detailsData, setDetailsData] = useState<{
        departments: any[];
        identifierDataElements: any[];
        hasLoadedInitialData: boolean;
    }>({
        departments: [],
        identifierDataElements: [],
        hasLoadedInitialData: false
    });
    
    // Store details form state in parent to persist across tab switches
    const [detailsFormState, setDetailsFormState] = useState<any>(null);

    // Publish functionality state
    const [successMessage, setSuccessMessage] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [wasPublishedInThisSession, setWasPublishedInThisSession] = useState(false);

    const DRAWER_WIDTH = theme.customLayout.drawerWidth;
    const HEADER_HEIGHT = theme.customLayout.headerHeight;
    const MEDIUM_GAP = theme.customLayout.mediumGap;

    useEffect(() => {
        if (collectionPoint) {
            // Reset changes saved flag when collection point changes
            setHasAnyChangesSaved(false);
            setPurposesFormState(null);
            
            // Reset current tab to Details tab (0) when modal opens or collection point changes
            setCurrentTab(0);
            
            // Always reset purposes data when collection point changes
            setPurposesData({
                purposes: [],
                purposeConstants: [],
                mappedPurposes: [],
                hasLoadedInitialData: false,
                hasAttemptedLoadMappedPurposes: false
            });
            
            // Only reset details data if we don't have it loaded
            setDetailsData(prevData => {
                if (!prevData.hasLoadedInitialData) {
                    return {
                        departments: [],
                        identifierDataElements: [],
                        hasLoadedInitialData: false
                    };
                }
                return prevData;
            });
            
            // Reset details form state when collection point changes
            setDetailsFormState(null);
        } else {
            // Clear all data when modal closes (collectionPoint is null)
            setPurposesData({
                purposes: [],
                purposeConstants: [],
                mappedPurposes: [],
                hasLoadedInitialData: false,
                hasAttemptedLoadMappedPurposes: false
            });
            setPurposesFormState(null);
            setDetailsData({
                departments: [],
                identifierDataElements: [],
                hasLoadedInitialData: false
            });
            setDetailsFormState(null);
        }
    }, [collectionPoint?.CollectionPointId, open]);







    // Add handler for closing
    const handleClose = () => {
        // If there were any changes saved, refresh the grid
        if (hasAnyChangesSaved && onSaved) {
            onSaved();
        }
        
        onClose();
    };

    // Handle publish collection point
    const handlePublish = async () => {
        if (!collectionPoint) return;

        // Check for unsaved changes
        if (hasUnsavedChanges) {
            const shouldProceed = window.confirm(
                'You have unsaved changes. Please save your changes before publishing.\n\n' +
                'Do you want to continue without saving?'
            );
            if (!shouldProceed) {
                return;
            }
        }

        const confirmMessage = 'Are you sure you want to publish this collection point?\n\n' +
            'Publishing will make this collection point available for consent collection.\n\n' +
            'Please ensure that you have:\n' +
            '• Mapped at least one purpose\n' +
            '• Mapped the identifier data element\n' +
            '• Reviewed all configurations\n\n' +
            'This action cannot be undone.';

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setIsPublishing(true);
            await collectionPointsService.publishCollectionPoint(collectionPoint.CollectionPointId);
            console.log('Publish successful, setting success message');
            const message = 'Collection point published successfully!';
            console.log('About to set success message:', message);
            setSuccessMessage(message);
            console.log('Success message set, current state should update on next render');
            setHasAnyChangesSaved(true);
            setWasPublishedInThisSession(true); // Mark as published in this session
            
            // Don't refresh the grid immediately - let the user see the success message
            // The grid will be refreshed when the panel is closed
            // Don't auto-close the panel - let the user see the success message and disabled button
        } catch (error: any) {
            // Show error in snackbar
            setSuccessMessage(error.response?.data?.error || 'Failed to publish collection point');
        } finally {
            setIsPublishing(false);
        }
    };










    const isPublished = !!collectionPoint?.IsPublished || wasPublishedInThisSession;

    // Debug: Log successMessage state
    console.log('EditCollectionPointPanel render - successMessage:', successMessage);

    // Debug: Track successMessage state changes
    useEffect(() => {
        console.log('successMessage state changed to:', successMessage);
    }, [successMessage]);

    return (
        <>
            <Snackbar
                open={!!successMessage}
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
                onClose={() => setSuccessMessage('')}
            >
                <Alert 
                    onClose={() => setSuccessMessage('')} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="edit-collection-point-modal-title"
                sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 1301 }}
            >
                <Box
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: theme.shadows[24],
                        p: theme.spacing(0),
                        width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
                        height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
                        position: 'absolute',
                        left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
                        top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
                        right: `${MEDIUM_GAP}px`,
                        bottom: `${MEDIUM_GAP}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                    }}
                >
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        minHeight: '64px'
                    }}>
                        <Typography id="edit-collection-point-modal-title" variant="h6" sx={{ color: theme.palette.text.primary }}>
                            Edit Entry Point
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePublish}
                            disabled={isPublishing || isPublished}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                },
                                '&.Mui-disabled': {
                                    bgcolor: theme.palette.action.disabledBackground,
                                    color: theme.palette.action.disabled,
                                }
                            }}
                        >
                            {isPublishing ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : isPublished ? (
                                'Collection Point Published'
                            ) : (
                                'Publish Collection Point'
                            )}
                        </Button>
                            <Button variant="outlined" color="inherit" onClick={handleClose}
                                sx={{
                                    borderColor: theme.palette.divider,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        bgcolor: theme.palette.action.hover,
                                    },
                                }}>
                                Close
                            </Button>
                        </Stack>
                    </Box>

                    {/* Tabs */}
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Tabs
                            value={currentTab}
                            onChange={(_, v) => setCurrentTab(v)}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab label="Details" />
                            <Tab label="Purposes" />
                            <Tab label="Data Elements" />
                            <Tab label="Preferences" />
                            <Tab label="Labels" />
                            <Tab label="Integrations" />
                            <Tab label="Events" />
                        </Tabs>
                    </Box>

                    {/* Tab Panels */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {currentTab === 0 && (
                            <DetailsTab 
                                collectionPoint={collectionPoint} 
                                isPublished={!!collectionPoint?.IsPublished}
                                onDetailsChanged={() => {
                                    setHasUnsavedChanges(false);
                                    if (onSaved) onSaved();
                                }}
                                onDetailsChangedUnsaved={() => setHasUnsavedChanges(true)}
                            />
                        )}
                        {currentTab === 1 && (
                            <PurposesTab
                                collectionPoint={collectionPoint}
                                isPublished={!!collectionPoint?.IsPublished}
                                onPurposesChanged={() => {
                                    // Mark that changes were saved
                                    setHasAnyChangesSaved(true);
                                }}
                                persistedFormState={purposesFormState}
                                onFormStateChange={setPurposesFormState}
                                persistedData={purposesData}
                                onDataChange={setPurposesData}
                            />
                        )}
                        
                        {currentTab === 2 && (
                            <DataElementsTab 
                                collectionPoint={collectionPoint} 
                                isPublished={!!collectionPoint?.IsPublished}
                                onDataElementsChanged={() => {
                                    // Mark that changes were saved
                                    setHasAnyChangesSaved(true);
                                }}
                            />
                        )}
                        {currentTab === 3 && (
                            <PreferencesTab 
                                collectionPoint={collectionPoint} 
                                isPublished={!!collectionPoint?.IsPublished}
                                onPreferencesChanged={() => {
                                    // Mark that changes were saved
                                    setHasAnyChangesSaved(true);
                                }}
                            />
                        )}
                        {currentTab === 4 && (
                            <LabelsTab 
                                collectionPoint={collectionPoint} 
                                isPublished={!!collectionPoint?.IsPublished}
                                onLabelsChanged={() => {
                                    // Mark that changes were saved
                                    setHasAnyChangesSaved(true);
                                }}
                            />
                        )}
                        {currentTab === 5 && (
                            <IntegrationsTab 
                                collectionPoint={collectionPoint}
                            />
                        )}
                        {currentTab === 6 && (
                            <EventsTab
                                collectionPoint={collectionPoint}
                                onEventsChanged={() => {
                                    setHasAnyChangesSaved(true);
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default EditCollectionPointPanel; 